import { useSearchParams, Link } from "react-router-dom";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import HeroMockup from "@/components/landing/HeroMockup";
import logo from "@/assets/logo.png";

const GleamAlternative = () => {
    const [searchParams] = useSearchParams();
    const isLPMode = searchParams.get("lp") === "true";

    return (
        <div className="min-h-screen bg-white font-sans text-gray-900">
            <SEO
                title="Best Gleam Alternative for Small Businesses (2026) | RaffleKit"
                description="Looking for a Gleam alternative? RaffleKit gives you the same core giveaway features for a fraction of the price. No setup fees, no hidden costs. Start free — no credit card required."
                canonical="https://rafflekit.app/gleam-alternative"
            />


            {isLPMode ? (
                <nav className="bg-white w-full border-b border-gray-100 py-4">
                    <div className="container mx-auto px-4 flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-2">
                            <img src={logo} alt="RaffleKit" className="w-10 h-10" />
                            <span className="font-heading font-bold text-xl text-gray-900">RaffleKit</span>
                        </Link>
                        <Button asChild size="sm" variant="accent" className="rounded-full px-6">
                            <Link to="/admin?mode=signup">Start Free — No Credit Card Required</Link>
                        </Button>
                    </div>
                </nav>
            ) : (
                <LandingNav />
            )}

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-white overflow-hidden">
                <div className="container mx-auto px-4 overflow-x-hidden">
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
                        <div className="min-w-0 text-center md:text-left">
                            <span className="inline-block bg-teal-50 text-teal-700 text-sm font-medium px-3 py-1 rounded-full">
                                Gleam Alternative
                            </span>
                            <h1 className="text-sm font-semibold text-primary/70 uppercase tracking-widest mb-3">
                                Best Gleam Alternative for Small Businesses (2026)
                            </h1>
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mt-4 break-words">
                                Run better giveaways for a fraction of the price.
                            </h2>
                            <p className="text-xl text-gray-500 mt-4 max-w-2xl mx-auto md:mx-0">
                                Gleam starts at $59/mo for the features most businesses actually need. RaffleKit gives you the same core functionality starting at $0 — with no confusing tiers, no hidden limits, and no surprise fees.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                                <Button asChild size="lg" variant="accent" className="rounded-full font-bold">
                                    <Link to="/admin?mode=signup">Start Free — No Credit Card Required</Link>
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="lg" 
                                    className="rounded-full bg-white transition-all hover:bg-gray-50"
                                    onClick={() => document.getElementById('comparison')?.scrollIntoView({ behavior: 'smooth' })}
                                >
                                    See the comparison ↓
                                </Button>
                            </div>
                        </div>
                        <div className="relative pt-4 md:pt-0 w-full overflow-hidden md:overflow-visible">
                            <div className="relative w-full flex justify-center items-start md:p-6 animate-float -mb-24 md:mb-0">
                                <div
                                    className="origin-top"
                                    style={{
                                        transform: 'scale(var(--gleam-mockup-scale, 1))',
                                        width: 520,
                                        maxWidth: 520,
                                        overflow: 'visible',
                                        minHeight: 380,
                                    }}
                                >
                                    <HeroMockup />
                                </div>
                                <style>{`
                                    :root { --gleam-mockup-scale: 1; }
                                    @media (max-width: 400px) { :root { --gleam-mockup-scale: 0.58; } }
                                    @media (min-width: 401px) and (max-width: 480px) { :root { --gleam-mockup-scale: 0.68; } }
                                    @media (min-width: 481px) and (max-width: 560px) { :root { --gleam-mockup-scale: 0.78; } }
                                    @media (min-width: 561px) and (max-width: 640px) { :root { --gleam-mockup-scale: 0.88; } }
                                    @media (min-width: 641px) and (max-width: 1023px) { :root { --gleam-mockup-scale: 0.95; } }
                                `}</style>
                            </div>
                        </div>
                    </div>

                    {/* Trust bar — OUTSIDE the grid, full width, centered */}
                    <div className="mt-6 md:mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-gray-400 justify-center max-w-6xl mx-auto px-4">
                        <span>✓ Free plan forever, no credit card required</span>
                        <span>✓ 3-month free trial on Starter</span>
                        <span>✓ Set up in under 5 minutes</span>
                    </div>
                </div>
            </section>

            {/* Section 2: Pricing Reality Check */}
            <section className="bg-gray-50 py-20 border-y border-gray-100">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <span className="inline-block bg-teal-50 text-teal-700 text-sm font-medium px-3 py-1 rounded-full">
                            The Real Cost of Gleam
                        </span>
                        <h2 className="text-3xl font-bold text-gray-900 mt-3">
                            Most small businesses end up paying $59–$179/mo on Gleam.
                        </h2>
                        <p className="text-gray-500 mt-2 max-w-2xl mx-auto">
                            Gleam's $19/mo Hobby plan sounds affordable — until you realize the features you actually need for a real campaign are locked behind the $59/mo Pro plan and above.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-12">
                        {/* Gleam Card */}
                        <div className="bg-rose-50 border border-rose-100 rounded-2xl p-8">
                            <span className="text-sm font-semibold text-rose-500 uppercase tracking-wide">Gleam</span>
                            <div className="flex items-baseline gap-1 mt-2">
                                <span className="text-4xl font-bold text-gray-900">$59–$179</span>
                                <span className="text-lg font-normal text-gray-500">/mo</span>
                            </div>
                            <ul className="mt-4 space-y-2 text-sm text-gray-600">
                                <li className="flex items-start gap-2">
                                    <span className="text-rose-400 font-bold">✗</span>
                                    <span>Hobby plan ($19/mo) missing: viral share actions, pixel tracking, post-entry redirect, branding removal</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-rose-400 font-bold">✗</span>
                                    <span>Pro plan ($59/mo) needed for: most real campaign features</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-rose-400 font-bold">✗</span>
                                    <span>Business plan ($179/mo) needed for: custom fields, custom CSS, team access</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-rose-400 font-bold">✗</span>
                                    <span>Add-on fees for extra features</span>
                                </li>
                            </ul>
                            <div className="mt-6 bg-rose-100 rounded-xl p-4 text-sm text-rose-700">
                                Real example: Running a campaign with viral sharing, pixel tracking, and your own branding? That's the Pro plan at $59/mo minimum — often more with add-ons.
                            </div>
                        </div>

                        {/* RaffleKit Card */}
                        <div className="bg-teal-50 border border-teal-100 rounded-2xl p-8">
                            <div className="flex items-center gap-2">
                                <img src={logo} alt="RaffleKit" className="w-6 h-6" />
                                <span className="text-sm font-semibold text-teal-700">RaffleKit</span>
                            </div>
                            <div className="flex items-baseline gap-1 mt-2">
                                <span className="text-4xl font-bold text-gray-900">$0–$29</span>
                                <span className="text-lg font-normal text-gray-500">/mo</span>
                            </div>
                            <ul className="mt-4 space-y-2 text-sm text-gray-600">
                                <li className="flex items-start gap-2">
                                    <span className="text-teal-500 font-bold">✓</span>
                                    <span>Free plan: 1 contest, 500 entries, always free</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-teal-500 font-bold">✓</span>
                                    <span>Starter ($9/mo): unlimited entries, 3 active contests, analytics, branding control — free for 3 months</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-teal-500 font-bold">✓</span>
                                    <span>Growth ($29/mo): unlimited everything, remove branding, custom fields</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-teal-500 font-bold">✓</span>
                                    <span>No add-on fees. No feature surprises.</span>
                                </li>
                            </ul>
                            <div className="mt-6 bg-teal-100 rounded-xl p-4 text-sm text-teal-700">
                                The same campaign that costs $59+/mo on Gleam runs on RaffleKit Starter for $9/mo — or free for your first 3 months.
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 text-center">
                        <div className="text-3xl font-bold text-gray-900 font-heading">Save up to $2,040/year</div>
                        <p className="text-gray-500 mt-1">compared to Gleam Pro</p>
                    </div>
                </div>
            </section>

            {/* SECTION 3 — Feature Comparison Table */}
            <section id="comparison" className="bg-white py-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <span className="inline-block bg-teal-50 text-teal-700 text-sm font-medium px-3 py-1 rounded-full">
                            Head to Head
                        </span>
                        <h2 className="text-3xl font-bold text-gray-900 mt-3">
                            Everything you need. Nothing you don't.
                        </h2>
                        <p className="text-gray-500 mt-2 max-w-2xl mx-auto">
                            An honest comparison of what each platform offers for small businesses running straightforward giveaways.
                        </p>
                    </div>

                    <div className="max-w-5xl mx-auto mt-12 overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-sm text-gray-600">
                                    <th className="py-4 px-6 font-bold">Feature</th>
                                    <th className="py-4 px-6 font-bold bg-teal-50 text-teal-700">RaffleKit Free</th>
                                    <th className="py-4 px-6 font-bold bg-teal-50 text-teal-700">RaffleKit Starter ($9)</th>
                                    <th className="py-4 px-6 font-bold">Gleam Hobby ($19)</th>
                                    <th className="py-4 px-6 font-bold">Gleam Pro ($59)</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                                {[
                                    ["Starting price", "$0/mo", "$9/mo", "$19/mo", "$59/mo"],
                                    ["Total contests stored", "Unlimited", "Unlimited", "Unlimited", "Unlimited"],
                                    ["Simultaneous active contests", "1", "3", "Unlimited", "Unlimited"],
                                    ["Entry limit", "500", "Unlimited", "Limited", "Limited"],
                                    ["Viral referral sharing", "✓", "✓", "✓", "✓"],
                                    ["Remove platform branding", "✗", "✓", "✗", "✓"],
                                    ["Analytics dashboard", "✗", <span className="text-teal-600 font-medium">Full dashboard</span>, "Basic", "Basic"],
                                    ["Fraud & abuse protection", "✗", "✓", "✗", "✗"],
                                    ["Custom entry actions", "✓", "✓", "Limited", "✓"],
                                    ["Embed on your site", "✓", "✓", "✓", "✓"],
                                    ["Post-entry redirect", "✗", "✓", "✗", "✓"],
                                    ["Platform API verification", "✗", "✗", "✓", "✓"],
                                    ["API access", "✗", "✗", "✗", "✗"],
                                    ["Setup time", "~5 min", "~5 min", "~20 min", "~20 min"]
                                ].map((row, i) => (
                                    <tr key={i} className={i % 2 === 1 ? "bg-gray-50/50" : "bg-white"}>
                                        <td className="py-3 px-6 font-medium text-gray-700">{row[0]}</td>
                                        <td className="py-3 px-6 text-center bg-teal-50/40">{row[1] === "✓" ? <span className="text-teal-500 font-bold">✓</span> : (row[1] === "✗" ? <span className="text-rose-400">✗</span> : row[1])}</td>
                                        <td className="py-3 px-6 text-center bg-teal-50/40">{row[2] === "✓" ? <span className="text-teal-500 font-bold">✓</span> : (row[2] === "✗" ? <span className="text-rose-400">✗</span> : row[2])}</td>
                                        <td className="py-3 px-6 text-center">{row[3] === "✓" ? <span className="text-teal-500 font-bold">✓</span> : (row[3] === "✗" ? <span className="text-rose-400">✗</span> : row[3])}</td>
                                        <td className="py-3 px-6 text-center">{row[4] === "✓" ? <span className="text-teal-500 font-bold">✓</span> : (row[4] === "✗" ? <span className="text-rose-400">✗</span> : row[4])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mt-6 text-xs text-gray-400 text-center">
                            Gleam pricing and features based on publicly available information. Last reviewed March 2026.
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 4 — Who Is This For */}
            <section className="bg-gray-50 py-20 border-y border-gray-100">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <span className="inline-block bg-teal-50 text-teal-700 text-sm font-medium px-3 py-1 rounded-full">
                            Honest Positioning
                        </span>
                        <h2 className="text-3xl font-bold text-gray-900 mt-3">
                            Is RaffleKit right for you?
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mt-12">
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-teal-500 text-xl">✓</span>
                                <h3 className="font-bold text-gray-900">RaffleKit is a great fit if...</h3>
                            </div>
                            <ul className="space-y-3 text-sm text-gray-600">
                                {[
                                    "You're a small business, creator, or solo operator",
                                    "You want to launch a giveaway quickly without a learning curve",
                                    "You need viral referral sharing, analytics, and fraud protection",
                                    "You're running 1–3 giveaways at a time",
                                    "You want predictable, simple pricing",
                                    "You're just getting started and want a free option"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span className="text-teal-500">✓</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-gray-400 text-xl">ⓘ</span>
                                <h3 className="font-bold text-gray-900">Gleam might be a better fit if...</h3>
                            </div>
                            <ul className="space-y-3 text-sm text-gray-600">
                                {[
                                    "You need deep integrations with Spotify, Steam, or Twitch",
                                    "You're running large-scale enterprise campaigns",
                                    "You need a dedicated API for custom development",
                                    "You require advanced team collaboration features",
                                    "You need custom CSS and full HTML email control"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span className="text-gray-400">→</span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <p className="mt-8 text-sm text-gray-400 text-center max-w-xl mx-auto italic">
                        "We'd rather you use the right tool for your needs. If that's RaffleKit, great — if not, no hard feelings."
                    </p>
                </div>
            </section>

            {/* SECTION 5 — Testimonials */}
            <section className="bg-white py-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <span className="inline-block bg-teal-50 text-teal-700 text-sm font-medium px-3 py-1 rounded-full">
                            What People Are Saying
                        </span>
                        <h2 className="text-3xl font-bold text-gray-900 mt-3">
                            Real businesses. Real results.
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12">
                        {[
                            {
                                quote: "I was paying way too much on another platform just to run a simple Instagram giveaway. RaffleKit got me set up in minutes and the referral tracking actually worked. My email list grew by over 400 people from one contest.",
                                name: "Wendy L.",
                                title: "Service Business Owner",
                                initials: "WL"
                            },
                            {
                                quote: "As a creator I needed something simple that didn't require a developer to set up. RaffleKit was exactly that — clean entry page, easy to share, and I could see who came in from referrals. Ran my first giveaway in under an hour.",
                                name: "Trish N.",
                                title: "Instagram Creator",
                                initials: "TN"
                            },
                            {
                                quote: "We tried Gleam first but the pricing jumped fast once we needed the features that actually mattered. RaffleKit covers everything we need for our product giveaways at a fraction of the cost. The analytics dashboard alone makes it worth it.",
                                name: "Jeffrey M.",
                                title: "Ecommerce Director",
                                initials: "JM"
                            }
                        ].map((t, i) => (
                            <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex flex-col justify-between">
                                <div>
                                    <div className="text-yellow-400 text-sm mb-3">★★★★★</div>
                                    <p className="text-sm text-gray-700 leading-relaxed italic">"{t.quote}"</p>
                                </div>
                                <div className="flex items-center gap-3 mt-6">
                                    <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">
                                        {t.initials}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-gray-900">{t.name}</div>
                                        <div className="text-xs text-gray-400">{t.title}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ADDITION 1 — SEO prose section */}
            <section className="bg-white py-16">
                <div className="max-w-3xl mx-auto px-4">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 font-heading">
                        Why small businesses are switching from Gleam to RaffleKit
                    </h2>
                    <div className="text-gray-600 text-base leading-relaxed space-y-4">
                        <p>
                            Gleam built its reputation as a comprehensive giveaway platform with deep integrations across social and gaming platforms. For large brands and enterprise teams, that depth is valuable. But for most small businesses, the complexity and cost create more friction than value. Most small business owners don't need Spotify pre-save campaigns or custom API access — they need a clean entry page, viral referral tracking, and a simple way to pick a winner.
                        </p>
                        <p>
                            The pricing reality is often the deciding factor. Gleam's Hobby plan at $19/mo sounds accessible, but the features most campaigns actually require — viral share actions, removing Gleam branding, and detailed analytics — are locked behind the Pro plan at $59/mo or the Business plan at $179/mo. A typical small business running regular giveaways can easily end up spending $700–$2,000 per year on Gleam before accounting for any add-ons. RaffleKit was built specifically to serve this segment better: Starter plan at $9/mo includes unlimited entries, analytics, fraud protection, and branding control — with a free 3-month trial to get started.
                        </p>
                        <p>
                            Simplicity is a core feature, not an afterthought. RaffleKit is designed to get a giveaway live in under 5 minutes. No developer needed, no complex setup, and no 30-tab feature documentation to read through. The contest builder is straightforward, entry actions are pre-built, and the public contest page is clean and conversion-optimized out of the box. For a small business owner wearing many hats, that time savings is real.
                        </p>
                        <p>
                            We maintain an honest positioning — RaffleKit isn't trying to replace Gleam for enterprise use cases. If you're a large brand running campaigns that require deep Twitch integration, custom CSS, or a development API, Gleam is likely the better tool. But if you're a small business, creator, or growing brand that wants to run effective giveaways without paying enterprise prices, RaffleKit is the smarter choice. The free plan exists for a reason — try it with zero commitment and see if it works for your audience.
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 6 — FAQ with Schema Markup */}
            <section className="bg-gray-50 py-20 border-y border-gray-100">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <span className="inline-block bg-teal-50 text-teal-700 text-sm font-medium px-3 py-1 rounded-full">
                            FAQ
                        </span>
                        <h2 className="text-3xl font-bold text-gray-900 mt-3">
                            Common questions
                        </h2>
                    </div>

                    <div className="max-w-2xl mx-auto mt-12 space-y-4">
                        {[
                            {
                                q: "Is there a free Gleam alternative?",
                                a: "Yes — RaffleKit offers a permanently free plan that lets you run one active giveaway with up to 500 entries. No credit card required, no trial expiry. It's genuinely free, forever."
                            },
                            {
                                q: "How much does Gleam actually cost for a real campaign?",
                                a: "Gleam's Hobby plan starts at $19/mo, but most real campaigns require features only available on the Pro plan ($59/mo) or higher — things like viral share actions, pixel tracking, and removing Gleam branding. Many small businesses end up paying $59–$179/mo."
                            },
                            {
                                q: "What's the difference between RaffleKit and Gleam?",
                                a: "Gleam is a powerful, feature-rich platform built for enterprise-scale campaigns with deep social integrations and API access. RaffleKit is built for small businesses that want to launch great giveaways quickly, without the complexity or cost. If you need Spotify pre-saves or a custom API, Gleam is the better fit. If you want viral referral giveaways up and running in minutes, RaffleKit is the smarter choice."
                            },
                            {
                                q: "Can I switch from Gleam to RaffleKit?",
                                a: "Yes. RaffleKit takes about 5 minutes to set up your first giveaway. You can start on the free plan and upgrade when you're ready. There's no contract and no long-term commitment."
                            },
                            {
                                q: "Does RaffleKit have all the features I need?",
                                a: "For most small business giveaways — yes. RaffleKit includes viral referral sharing, entry actions, analytics, fraud protection, branding control, and embed support. If you need deep integrations with platforms like Twitch or Steam, or a custom development API, Gleam may be a better fit."
                            },
                            {
                                q: "Is RaffleKit good for Instagram giveaways?",
                                a: "Yes. RaffleKit supports Instagram follow and engagement entry actions, referral sharing, and a clean public contest page you can link to from your bio or Stories. You don't need to manage entries manually — RaffleKit tracks everything automatically and lets you pick a verified winner when the contest ends."
                            },
                            {
                                q: "What happens when my free trial ends?",
                                a: "RaffleKit's Starter plan includes a free 3-month trial — no charge until the trial ends. After 3 months, your plan activates at $9/mo. If you decide Starter isn't for you, you can downgrade to the free plan at any time. Your data and past contests are never deleted."
                            },
                            {
                                q: "Can I embed a RaffleKit giveaway on my website?",
                                a: "Yes. Every RaffleKit contest can be embedded directly on your website or landing page using a simple embed code. Your visitors can enter without leaving your site, which keeps your traffic and improves conversion rates."
                            }
                        ].map((faq, i) => (
                            <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                                <h3 className="font-semibold text-gray-900 text-base">{faq.q}</h3>
                                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{faq.a}</p>
                            </div>
                        ))}
                    </div>

                    <script type="application/ld+json" dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "FAQPage",
                            "mainEntity": [
                                {
                                    "@type": "Question",
                                    "name": "Is there a free Gleam alternative?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "Yes — RaffleKit offers a permanently free plan that lets you run one active giveaway with up to 500 entries. No credit card required, no trial expiry. It's genuinely free, forever."
                                    }
                                },
                                {
                                    "@type": "Question",
                                    "name": "How much does Gleam actually cost for a real campaign?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "Gleam's Hobby plan starts at $19/mo, but most real campaigns require features only available on the Pro plan ($59/mo) or higher — things like viral share actions, pixel tracking, and removing Gleam branding. Many small businesses end up paying $59–$179/mo."
                                    }
                                },
                                {
                                    "@type": "Question",
                                    "name": "What's the difference between RaffleKit and Gleam?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "Gleam is a powerful, feature-rich platform built for enterprise-scale campaigns with deep social integrations and API access. RaffleKit is built for small businesses that want to launch great giveaways quickly, without the complexity or cost. If you need Spotify pre-saves or a custom API, Gleam is the better fit. If you want viral referral giveaways up and running in minutes, RaffleKit is the smarter choice."
                                    }
                                },
                                {
                                    "@type": "Question",
                                    "name": "Can I switch from Gleam to RaffleKit?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "Yes. RaffleKit takes about 5 minutes to set up your first giveaway. You can start on the free plan and upgrade when you're ready. There's no contract and no long-term commitment."
                                    }
                                },
                                {
                                    "@type": "Question",
                                    "name": "Does RaffleKit have all the features I need?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "For most small business giveaways — yes. RaffleKit includes viral referral sharing, entry actions, analytics, fraud protection, branding control, and embed support. If you need deep integrations with platforms like Twitch or Steam, or a custom development API, Gleam may be a better fit."
                                    }
                                },
                                {
                                    "@type": "Question",
                                    "name": "Is RaffleKit good for Instagram giveaways?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "Yes. RaffleKit supports Instagram follow and engagement entry actions, referral sharing, and a clean public contest page you can link to from your bio or Stories. You don't need to manage entries manually — RaffleKit tracks everything automatically and lets you pick a verified winner when the contest ends."
                                    }
                                },
                                {
                                    "@type": "Question",
                                    "name": "What happens when my free trial ends?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "RaffleKit's Starter plan includes a free 3-month trial — no charge until the trial ends. After 3 months, your plan activates at $9/mo. If you decide Starter isn't for you, you can downgrade to the free plan at any time. Your data and past contests are never deleted."
                                    }
                                },
                                {
                                    "@type": "Question",
                                    "name": "Can I embed a RaffleKit giveaway on my website?",
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": "Yes. Every RaffleKit contest can be embedded directly on your website or landing page using a simple embed code. Your visitors can enter without leaving your site, which keeps your traffic and improves conversion rates."
                                    }
                                }
                            ]
                        })
                    }} />
                </div>
            </section>

            {/* SECTION 7 — Final CTA */}
            <section className="bg-gradient-to-br from-teal-600 to-teal-700 py-20">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white">
                        Run your first giveaway today.
                    </h2>
                    <p className="text-white/80 mt-3 text-lg">
                        Free forever. No credit card required. Set up in minutes.
                    </p>
                    <div className="mt-8">
                        <Button asChild size="lg" className="bg-white text-teal-700 font-bold hover:bg-gray-50 rounded-full h-14 px-10 text-lg">
                            <Link to="/admin?mode=signup">Start Free — It's $0 Forever</Link>
                        </Button>
                    </div>
                    <Link to="/admin?mode=signup" className="inline-block text-white/60 text-sm mt-4 underline hover:text-white transition-colors">
                        Or try Starter free for 3 months →
                    </Link>
                </div>
            </section>


            {isLPMode ? (
                <footer className="py-12 border-t border-gray-100 text-center text-sm text-gray-400">
                    <div className="container mx-auto px-4">
                        © {new Date().getFullYear()} RaffleKit · <Link to="/privacy" className="hover:text-gray-600 underline transition-colors">Privacy Policy</Link> · <Link to="/terms" className="hover:text-gray-600 underline transition-colors">Terms</Link>
                    </div>
                </footer>
            ) : (
                <LandingFooter />
            )}
        </div>
    );
};

export default GleamAlternative;
